import { EFaceType } from '../entities/type';
import editorEvent, { EditorEventName } from '../event/editorEvent';
import { modelManager } from '../manager/entityManager';
import { globalStores, TGlobalStores } from '../stores';
import { EEntityType, INotHistoryParams } from '../types';
import { EOperationType } from '../types/operation';
import { ELayoutType, IFullLayoutResultData } from '@/service/layout/types';
import { restoreRegionInfo, clearRegion } from '@/editor/utils/layout/apply';
import { SeparateWallData } from '../data/separateWall/SeparateWall';
import { IFurniture } from '../data/model/furniture';
import { EDoorWindowSide, EDoorWindowType, IWindowData } from '../data/model/model';
import { IDrawSegParams } from '@/pages/development/layout-task/utils/payload';
import { IConvertedCadData, ICadImportSummary, ICadSkippedItem } from '@/service/import/convert';
import { initDrawWallInfo } from '../data/wall/utils';
import { LineSeg2d } from '../math/LineSeg2d';
import { getWindowDirectedDistance } from '../utils/window';
import { getUniqueId } from '../utils';
import { DEFAULT_DOOR_OFFGROUND, DEFAULT_WINDOW_OFFGROUND } from '../constant/entity';

interface IApplyFullLayoutResultPayload {
  type: 'full';
  roomId: string;
  layoutType: ELayoutType;
  furnitureList: IFurniture[];
  sepWallList: SeparateWallData[];
  doorWindowList: IWindowData[];
  regionList: IFullLayoutResultData['outPutMessage'][number]['regionDatas'];
}

interface IApplyPartialLayoutResultPayload {
  type: 'partial';
  regionId: string;
  furnitureList: IFurniture[];
}

type TApplyAutoLayoutResultPayload = IApplyFullLayoutResultPayload | IApplyPartialLayoutResultPayload;

export class CompositeOperation {
  private _faceStore: TGlobalStores['faceStore'];
  private _regionStore: TGlobalStores['regionStore'];
  private _roomStore: TGlobalStores['roomStore'];
  private _history: TGlobalStores['history'];
  private _assemblyStore: TGlobalStores['assemblyStore'];
  private _modelStore: TGlobalStores['modelStore'];

  private _gs: TGlobalStores;

  constructor(gs: TGlobalStores) {
    this._gs = gs;
    this._faceStore = gs.faceStore;
    this._regionStore = gs.regionStore;
    this._roomStore = gs.roomStore;
    this._assemblyStore = gs.assemblyStore;
    this._modelStore = gs.modelStore;
    this._history = gs.history;
  }

  private $history_clearMaterials() {
    this._history.startOperation();

    const columnFaceDatas = this._faceStore.getColumnFaceDatas();
    const wallFaceDatas = this._faceStore.getWallFaceDatas();
    const sepWallFaceDatas = this._faceStore.getSepWallFaceDatas(); // TODO: bug 要排除玻璃面
    const regionDatas = this._regionStore.getRegionDatas();

    const defaultFaceMaterial = globalStores.getDefaultMaterial({ type: EFaceType.WallFace });
    const defaultFloorMaterial = globalStores.getDefaultMaterial({ type: EFaceType.FloorFace });
    for (const faceData of [...columnFaceDatas, ...wallFaceDatas, ...sepWallFaceDatas]) {
      this._faceStore.updateFaceMaterial(faceData.id, defaultFaceMaterial);
    }

    for (const regionData of regionDatas) {
      const roomRegionDatas = this._regionStore.getRoomRegions(regionData.relateRoomId);
      if (roomRegionDatas.length === 1) {
        // 只有一个区域，需要同步到房间
        this._roomStore.updateRoomBaseData(regionData.relateRoomId, {
          materialInfo: { id: defaultFloorMaterial.id }
        });
      }

      this._regionStore.updateRegionBaseData(regionData.id, {
        materialInfo: { id: defaultFloorMaterial.id }
      });
    }

    // 将操作记录压栈
    this._history.completeOperation(EOperationType.ClearMaterials);
  }

  private $history_clearAll() {
    this._history.startOperation();

    globalStores.clearAll();

    // 将操作记录压栈
    this._history.completeOperation(EOperationType.ClearAll);
  }

  private $history_setLayerHeight(height: number) {
    this._history.startOperation();
    globalStores.paramStore.updateHistoryParams({ layerHeight: height });

    this._gs.wallStore.getWallDatas().forEach((w) => this._gs.wallStore.updateWallBaseData(w.id, { height }));

    this._gs.separateWallStore
      .getSepDatas({ onlyNotBase: true })
      .forEach((s) => this._gs.separateWallStore.updateSepBaseData(s.id, { height }, false));

    this._gs.structStore.getColumnDatas().forEach((c) => this._gs.structStore.updateColumnData(c.id, { height }));

    globalStores.refreshData();

    this._history.completeOperation(EOperationType.SetLayerHeight);
  }

  clearAll() {
    globalStores.editorStateStore.clearSelected();
    globalStores.editorStateStore.clearHoverEntity();

    this.$history_clearAll();
    modelManager.updateEntity();
    editorEvent.emit(EditorEventName.ENTITY_CLEAR, 'all');
  }

  clearMaterials() {
    this.$history_clearMaterials();

    modelManager.updateEntity();
    editorEvent.emit(EditorEventName.REPLACE_END);
    editorEvent.emit(EditorEventName.ENTITY_CLEAR, 'material');
  }

  setLayerHeight(height: number) {
    const currentHeight = globalStores.paramStore.layerHeight;
    if (height === currentHeight) return;

    this.$history_setLayerHeight(height);

    modelManager.updateEntity();
  }

  setNotHistoryParams(params: Partial<INotHistoryParams>) {
    globalStores.paramStore.updateNotHistoryParams(params);
  }

  private $history_applyAutoLayoutResult(payload: TApplyAutoLayoutResultPayload) {
    this._history.startOperation();
    const illegalSepWalls: SeparateWallData[] = [];
    let generatedPreview: IDrawSegParams | undefined = undefined;
    let unusedRegion: unknown[] = [];

    const { type, furnitureList } = payload;

    if (type === 'partial') {
      // 清除分区
      clearRegion(payload.regionId);
    }

    if (type === 'full') {
      // 清除房间内所有数据
      this._gs.roomOperation.dangerouslyClearRoom(payload.roomId);
    }

    // 添加家具
    furnitureList.forEach((furnitureData) => {
      this._gs.modelStore.addFurnitureData(furnitureData);
    });

    // 只有房间才能进行全局自动布局，才会生成分区和门窗
    if (type === 'full') {
      // 添加隔墙
      payload.sepWallList.forEach((separateWallData) => {
        const { isLegal, reason } = this._gs.separateWallOperation.isLegal(
          separateWallData.start,
          separateWallData.end,
          payload.roomId
        );
        // TODO: 验证隔墙是否合法，后续算法修复后可去除
        if (isLegal) {
          this._gs.separateWallStore.addSepData(separateWallData, false);
        } else {
          illegalSepWalls.push(separateWallData);
          console.warn('自动布局结果中的隔墙不合法，无法添加', separateWallData, reason);
        }
      });
      // 恢复区域信息
      const recordInfo = restoreRegionInfo(payload.regionList, payload.roomId, payload.layoutType);

      if (recordInfo) {
        generatedPreview = recordInfo.previewData;
        unusedRegion = recordInfo.unusedRegion;
      }
      // 添加门窗
      payload.doorWindowList.forEach((doorWindowData) => {
        this._gs.modelStore.addWindowData(doorWindowData);
      });
    }
    // 刷新面数据
    this._gs.refreshData();

    this._history.completeOperation(EOperationType.ApplyAutoLayoutResult);
    return {
      illegalSepWalls,
      generatedPreview: generatedPreview ? generatedPreview : undefined,
      unusedRegion
    };
  }

  applyAutoLayoutResult(payload: TApplyAutoLayoutResultPayload) {
    const data = this.$history_applyAutoLayoutResult(payload);
    modelManager.updateEntity();
    return data;
  }

  private $history_applyCadRecognition(payload: IConvertedCadData): ICadImportSummary {
    this._history.startOperation();

    const skipped: ICadSkippedItem[] = [...payload.skipped];
    const warnings: string[] = [...payload.warnings];
    let wallCount = 0;
    let doorCount = 0;
    let windowCount = 0;
    let columnCount = 0;

    // 事务内单个构件失败只记录不中断，保证 startOperation/completeOperation 成对
    for (const wall of payload.walls) {
      try {
        this._gs.wallStore.addWallData({
          id: wall.id,
          baseData: wall.baseData,
          drawWallInfo: initDrawWallInfo(wall.baseData)
        });
        wallCount++;
      } catch (e) {
        skipped.push({ kind: 'wall', cadId: wall.cadId, reason: 'store add failed' });
        console.warn('CAD导入：墙体添加失败', wall.cadId, e);
      }
    }

    for (const opening of payload.openings) {
      try {
        // 门窗距离必须基于 store 中的实际墙体计算（addWallData 可能已纠正端点）
        const wallData = this._gs.wallStore.getWallData(opening.editorWallId);
        if (!wallData) {
          skipped.push({ kind: opening.kind, cadId: opening.cadId, reason: 'host wall missing' });
          continue;
        }

        const seg = new LineSeg2d(wallData.baseData.start, wallData.baseData.end);
        const width = Math.min(opening.width, seg.length);
        if (width <= 0) {
          skipped.push({ kind: opening.kind, cadId: opening.cadId, reason: 'degenerate host wall' });
          continue;
        }

        const rawDistance = getWindowDirectedDistance(seg, opening.centerOnWall);
        const distance = Math.min(Math.max(rawDistance, width / 2), seg.length - width / 2);
        const modelId = this._gs.modelStore.getWindowDoorDefaultBrandGoodId(opening.type);
        if (!modelId) warnings.push(`no default model for ${opening.type} (${opening.cadId})`);
        const model = this._gs.modelStore.getModel(modelId);

        this._gs.modelStore.addWindowData({
          id: getUniqueId(),
          type: opening.type,
          modelId: modelId ?? '',
          width,
          height: model?.properties.height ?? 2000,
          offGround: opening.type === EDoorWindowType.window ? DEFAULT_WINDOW_OFFGROUND : DEFAULT_DOOR_OFFGROUND,
          distance,
          side: EDoorWindowSide.right,
          flip: false,
          relativeWallInfo: { relativeWallId: wallData.id, wallType: EEntityType.Wall },
          center: { x: 0, y: 0 },
          rotation: 0,
          thickness: wallData.baseData.thickness
        });
        if (opening.kind === 'door') doorCount++;
        else windowCount++;
      } catch (e) {
        skipped.push({ kind: opening.kind, cadId: opening.cadId, reason: 'store add failed' });
        console.warn('CAD导入：门窗添加失败', opening.cadId, e);
      }
    }

    for (const column of payload.columns) {
      try {
        this._gs.structStore.addColumnData(column);
        columnCount++;
      } catch (e) {
        skipped.push({ kind: 'column', cadId: column.id, reason: 'store add failed' });
        console.warn('CAD导入：柱子添加失败', column.id, e);
      }
    }

    this._gs.refreshData();
    this._faceStore.refreshColumnFaceDadas();

    this._history.completeOperation(EOperationType.CadRecognitionImport);

    return { wallCount, doorCount, windowCount, columnCount, skipped, warnings };
  }

  applyCadRecognition(payload: IConvertedCadData): ICadImportSummary {
    const summary = this.$history_applyCadRecognition(payload);
    modelManager.updateEntity();
    return summary;
  }

  private $history_clearFurnitures() {
    this._history.startOperation();

    this._modelStore.clearAllFurniture();
    // 组合只是家具的分组，家具清空后不能留下没有成员的组合
    this._assemblyStore.clearAll();

    this._history.completeOperation(EOperationType.FurnitureDelAll);
  }

  clearFurnitures() {
    editorEvent.emit(EditorEventName.REPLACE_END);

    // 必须在删数据之前判定：$history_clearFurnitures 压栈时发出的 HISTORY_CHANGE 会走到
    // refreshFurnitureMultiSelected，把已删掉的家具从 multiSelectTemporary 里剔除，
    // 之后再读就分不出多选里原本有没有家具了
    const { selected, multiSelectTemporary } = globalStores.editorStateStore;
    const isFurnitureSelected =
      selected.type === EEntityType.Furniture ||
      selected.type === EEntityType.Assembly ||
      (selected.type === EEntityType.MultiModel &&
        !!multiSelectTemporary?.modelInfos.some((item) =>
          [EEntityType.Furniture, EEntityType.Assembly].includes(item.type)
        ));

    this.$history_clearFurnitures();

    modelManager.furnitureManager.update();
    modelManager.assemblyManager.update();

    if (isFurnitureSelected) {
      globalStores.editorStateStore.clearSelected();
    }
    globalStores.editorStateStore.clearHoverEntity();
    editorEvent.emit(EditorEventName.ENTITY_CLEAR, 'furniture');
  }
}
